import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

// Fallback development credentials (use only for local development / staging)
const FALLBACK_ADMIN_EMAIL = "admin";
const FALLBACK_ADMIN_PASSWORD = "123456";

const createFallbackSession = () => ({
  user: {
    id: "00000000-0000-0000-0000-000000000000",
    email: FALLBACK_ADMIN_EMAIL,
    aud: "authenticated",
    role: "admin",
    app_metadata: {},
    user_metadata: {},
    identities: [],
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  access_token: "",
  token_type: "bearer",
  expires_in: 0,
  expires_at: 0,
  refresh_token: "",
  provider_token: null,
  provider_refresh_token: null,
});

interface AuthContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  isAdmin: boolean;
  isFallbackAdmin: boolean;
  signUp: (identifier: string, password: string, fullName: string, role?: string) => Promise<{ error: Error | null }>;
  signIn: (identifier: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const isPhoneIdentifier = (value: string) => /^\+?[0-9]{7,15}$/.test(value.trim());

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isFallbackAdmin, setIsFallbackAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for fallback admin flag first (persists across reloads)
    const isFallbackAdminActive = localStorage.getItem("_fallback_admin_active") === "true";
    if (isFallbackAdminActive) {
      const fallbackSession = createFallbackSession();
      setSession(fallbackSession as any);
      setUser(fallbackSession.user);
      setIsAdmin(true);
      setIsFallbackAdmin(true);
      setLoading(false);
      return;
    }

    // Set up auth state listener BEFORE checking session
    const { data } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setIsFallbackAdmin(false);
        setLoading(false);

        // Determine admin role by querying user_roles table directly
        if (session?.user?.email === FALLBACK_ADMIN_EMAIL) {
          setIsAdmin(true);
        } else if (session?.user?.id) {
          const { data: roleData, error } = await supabase
            .from("user_roles")
            .select("role")
            .eq("user_id", session.user.id)
            .eq("role", "admin")
            .maybeSingle();
          setIsAdmin(!!roleData && !error);
        } else {
          setIsAdmin(false);
        }
      }
    );

    const subscription = data?.subscription;

    // Check for existing session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setIsFallbackAdmin(false);

      if (session?.user?.email === FALLBACK_ADMIN_EMAIL) {
        setIsAdmin(true);
      } else if (session?.user?.id) {
        const { data, error } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", session.user.id)
          .eq("role", "admin")
          .maybeSingle();
        setIsAdmin(!!data && !error);
      } else {
        setIsAdmin(false);
      }

      setLoading(false);
    });

    return () => subscription?.unsubscribe();
  }, []);

  const signUp = async (identifier: string, password: string, fullName: string, role?: string) => {
    const normalizedIdentifier = identifier.trim();
    const commonData = {
      full_name: fullName,
      requested_role: role || "user",
    };

    try {
      const { error } = isPhoneIdentifier(normalizedIdentifier)
        ? await supabase.auth.signUp({
            phone: normalizedIdentifier,
            password,
            options: {
              data: commonData,
            },
          })
        : await supabase.auth.signUp({
            email: normalizedIdentifier,
            password,
            options: {
              emailRedirectTo: window.location.origin,
              data: commonData,
            },
          });

      return { error };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signIn = async (identifier: string, password: string) => {
    const normalizedIdentifier = identifier.trim();

    // Fallback local dev admin login (works even if Supabase is down)
    if (normalizedIdentifier === FALLBACK_ADMIN_EMAIL && password === FALLBACK_ADMIN_PASSWORD) {
      const fallbackSession = createFallbackSession();
      setSession(fallbackSession as any);
      setUser(fallbackSession.user);
      setIsAdmin(true);
      setIsFallbackAdmin(true);
      // Store fallback flag in localStorage so it persists across page reloads
      localStorage.setItem("_fallback_admin_active", "true");
      return { error: null };
    }

    try {
      const { error } = isPhoneIdentifier(normalizedIdentifier)
        ? await supabase.auth.signInWithPassword({
            phone: normalizedIdentifier,
            password,
          })
        : await supabase.auth.signInWithPassword({
            email: normalizedIdentifier,
            password,
          });

      if (!error) {
        localStorage.removeItem("_fallback_admin_active");
        setIsFallbackAdmin(false);
      }
      return { error };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    localStorage.removeItem("_fallback_admin_active");
    setSession(null);
    setUser(null);
    setIsAdmin(false);
    setIsFallbackAdmin(false);
  };

  return (
    <AuthContext.Provider value={{ session, user, isAdmin, isFallbackAdmin, loading, signUp, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
